// ================================================================
// 貨幣上下文
// ================================================================
// 版本：1.0
// 日期：2026-01-21
// 功能：全域貨幣狀態管理，支援多貨幣顯示
// ================================================================

import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
} from 'react';
import {
  Currency,
  ExchangeRate,
  SUPPORTED_CURRENCIES,
  getExchangeRates,
  getUserCurrency,
  setUserCurrency,
  getCurrencyInfo,
} from '../api/currency';
import { AuthContext } from './AuthContext';

// ================================================================
// 類型定義
// ================================================================

export interface CurrencyContextType {
  // 狀態
  preferredCurrency: string;
  currencies: Currency[];
  exchangeRates: ExchangeRate[];
  isLoading: boolean;

  // 操作
  setCurrency: (currencyCode: string) => Promise<void>;
  convertPrice: (amountInNTD: number) => number;
  formatPrice: (amountInNTD: number, showSymbol?: boolean) => string;
  getCurrencySymbol: () => string;
  getCurrencyFlag: () => string;
}

// ================================================================
// 建立上下文
// ================================================================

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

// ================================================================
// Provider Props
// ================================================================

interface CurrencyProviderProps {
  children: ReactNode;
}

// ================================================================
// 貨幣提供者組件
// ================================================================

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [preferredCurrency, setPreferredCurrency] = useState<string>('NTD');
  const [exchangeRates, setExchangeRates] = useState<ExchangeRate[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const authContext = useContext(AuthContext);
  const isAuthenticated = authContext?.isAuthenticated || false;

  // ================================================================
  // 初始化：載入匯率和用戶偏好
  // ================================================================

  useEffect(() => {
    const initializeCurrency = async () => {
      setIsLoading(true);
      try {
        // 載入匯率
        const ratesResponse = await getExchangeRates();
        if (ratesResponse.success && ratesResponse.rates) {
          // 將後端的 rate_to_ntd 映射為前端的 rate
          const normalizedRates = ratesResponse.rates.map((r: ExchangeRate) => ({
            ...r,
            rate: r.rate_to_ntd || r.rate || 1,
          }));
          setExchangeRates(normalizedRates);
        }

        // 如果用戶已登入，載入偏好貨幣
        if (isAuthenticated) {
          try {
            const userCurrencyResponse = await getUserCurrency();
            if (userCurrencyResponse.success && userCurrencyResponse.preferred_currency) {
              setPreferredCurrency(userCurrencyResponse.preferred_currency);
            }
          } catch (error) {
            console.warn('無法載入用戶偏好貨幣:', error);
          }
        } else {
          // 未登入時，嘗試從 localStorage 讀取
          const savedCurrency = localStorage.getItem('preferred_currency');
          if (savedCurrency && SUPPORTED_CURRENCIES.some((c) => c.code === savedCurrency)) {
            setPreferredCurrency(savedCurrency);
          }
        }
      } catch (error) {
        console.error('初始化貨幣失敗:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeCurrency();
  }, [isAuthenticated]);

  // ================================================================
  // 設定貨幣
  // ================================================================

  const setCurrency = useCallback(
    async (currencyCode: string) => {
      if (!SUPPORTED_CURRENCIES.some((c) => c.code === currencyCode)) {
        console.error('不支援的貨幣:', currencyCode);
        return;
      }

      setPreferredCurrency(currencyCode);
      localStorage.setItem('preferred_currency', currencyCode);

      // 如果用戶已登入，同步到後端
      if (isAuthenticated) {
        try {
          await setUserCurrency(currencyCode);
        } catch (error) {
          console.warn('無法保存用戶偏好貨幣:', error);
        }
      }
    },
    [isAuthenticated]
  );

  // ================================================================
  // 轉換價格（從 NTD 轉換到偏好貨幣）
  // ================================================================

  const convertPrice = useCallback(
    (amountInNTD: number): number => {
      if (preferredCurrency === 'NTD') {
        return amountInNTD;
      }

      const rate = exchangeRates.find((r) => r.currency_code === preferredCurrency);
      if (!rate || rate.rate === 0) {
        return amountInNTD;
      }

      return amountInNTD / rate.rate;
    },
    [preferredCurrency, exchangeRates]
  );

  // ================================================================
  // 格式化價格
  // ================================================================

  const formatPrice = useCallback(
    (amountInNTD: number, showSymbol: boolean = true): string => {
      const convertedAmount = convertPrice(amountInNTD);
      const currency = getCurrencyInfo(preferredCurrency);

      if (!currency) {
        return `${convertedAmount}`;
      }

      // 根據貨幣決定小數位數和格式化
      // NTD, JPY, CNY: 無小數（整數）
      // USD, EUR: 1 位小數
      let formattedAmount: string;
      if (preferredCurrency === 'NTD' || preferredCurrency === 'JPY' || preferredCurrency === 'CNY') {
        formattedAmount = Math.round(convertedAmount).toString();
      } else {
        // USD, EUR: 1 位小數
        formattedAmount = convertedAmount.toFixed(1);
      }

      if (showSymbol) {
        return `${currency.symbol} ${formattedAmount}`;
      }

      return formattedAmount;
    },
    [preferredCurrency, convertPrice]
  );

  // ================================================================
  // 取得當前貨幣符號
  // ================================================================

  const getCurrencySymbol = useCallback((): string => {
    const currency = getCurrencyInfo(preferredCurrency);
    return currency?.symbol || 'NT$';
  }, [preferredCurrency]);

  // ================================================================
  // 取得當前貨幣國旗
  // ================================================================

  const getCurrencyFlag = useCallback((): string => {
    const currency = getCurrencyInfo(preferredCurrency);
    return currency?.flag || '🇹🇼';
  }, [preferredCurrency]);

  // ================================================================
  // Context Value
  // ================================================================

  const value: CurrencyContextType = {
    preferredCurrency,
    currencies: SUPPORTED_CURRENCIES,
    exchangeRates,
    isLoading,
    setCurrency,
    convertPrice,
    formatPrice,
    getCurrencySymbol,
    getCurrencyFlag,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

// ================================================================
// Custom Hook
// ================================================================

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export { CurrencyContext };
